import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    IconButton,
    Tooltip,
    Dialog,
    DialogContent,
    CircularProgress,
    Alert,
    TextField,
    Button,
    DialogTitle,
    DialogActions
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    ZoomIn as ZoomInIcon
} from '@mui/icons-material';
import api from '../services/api';

// ============================================================================
// Interfaces
// ============================================================================
interface GuestImage {
    id: number;
    image_url: string;
    caption?: string;
    display_order: number;
    created_at: string;
}

interface GuestImageGalleryProps {
    guestId?: string | number;
    onAddImage?: () => void;
    apiEndpoint?: string; // Default to 'guest_images', can be 'cards' or others
    title?: string; // Default to 'Photos', can be 'Cards Received' or others
}

// ============================================================================
// Component
// ============================================================================
export const GuestImageGallery: React.FC<GuestImageGalleryProps> = ({
    guestId,
    onAddImage,
    apiEndpoint = 'guest_images',
    title = 'Photos'
}) => {
    const [images, setImages] = useState<GuestImage[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedImage, setSelectedImage] = useState<GuestImage | null>(null);
    const [editingCaption, setEditingCaption] = useState<number | null>(null);
    const [newCaption, setNewCaption] = useState('');

    useEffect(() => {
        if (guestId) {
            fetchImages();
        }
    }, [guestId]);

    const fetchImages = async () => {
        if (!guestId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const params = new URLSearchParams({
                'filter[guest_id]': guestId.toString()
            });
            
            const response = await api.get(`/${apiEndpoint}?${params}`);
            const sortedImages = response.data.sort((a: GuestImage, b: GuestImage) => 
                (a.display_order || 0) - (b.display_order || 0)
            );
            setImages(sortedImages);
        } catch (err) {
            console.error(`Error fetching ${apiEndpoint}:`, err);
            setError('Failed to load images');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteImage = async (imageId: number) => {
        if (!window.confirm('Are you sure you want to delete this image?')) {
            return;
        }

        try {
            await api.delete(`/${apiEndpoint}/${imageId}`);
            setImages(images.filter(img => img.id !== imageId));
        } catch (err) {
            console.error(`Error deleting ${apiEndpoint}:`, err);
            setError('Failed to delete image');
        }
    };

    const handleUpdateCaption = async (imageId: number, caption: string) => {
        try {
            await api.put(`/${apiEndpoint}/${imageId}`, { caption });
            setImages(images.map(img => 
                img.id === imageId ? { ...img, caption } : img
            ));
            setEditingCaption(null);
            setNewCaption('');
        } catch (err) {
            console.error(`Error updating ${apiEndpoint}:`, err);
            setError('Failed to update caption');
        }
    };

    const startEditingCaption = (image: GuestImage) => {
        setEditingCaption(image.id);
        setNewCaption(image.caption || '');
    };

    const cancelEditingCaption = () => {
        setEditingCaption(null);
        setNewCaption('');
    };

    if (!guestId) {
        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {title}
                </Typography>
                <Alert severity="info">
                    Save the guest record first to add {title.toLowerCase()}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    {title} ({images.length})
                </Typography>
                {onAddImage && (
                    <Tooltip title={`Add ${title.slice(0, -1)}`}>
                        <IconButton onClick={onAddImage} color="primary">
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            ) : images.length === 0 ? (
                <Alert severity="info">
                    No {title.toLowerCase()} uploaded yet
                </Alert>
            ) : (
                <Grid container spacing={2}>
                    {images.map((image) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                        transform: 'scale(1.02)',
                                    }
                                }}
                            >
                                <Box sx={{ position: 'relative' }}>
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={image.image_url}
                                        alt={image.caption || 'Guest photo'}
                                        sx={{ 
                                            objectFit: 'cover',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setSelectedImage(image)}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            display: 'flex',
                                            gap: 0.5,
                                            opacity: 0.8,
                                            '&:hover': { opacity: 1 }
                                        }}
                                    >
                                        <Tooltip title="View Full Size">
                                            <IconButton
                                                size="small"
                                                onClick={() => setSelectedImage(image)}
                                                sx={{ 
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                                                }}
                                            >
                                                <ZoomInIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Photo">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteImage(image.id)}
                                                sx={{ 
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                                <CardContent sx={{ flexGrow: 1, p: 1 }}>
                                    {editingCaption === image.id ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={newCaption}
                                                onChange={(e) => setNewCaption(e.target.value)}
                                                placeholder="Add a caption..."
                                                multiline
                                                rows={2}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => handleUpdateCaption(image.id, newCaption)}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={cancelEditingCaption}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    flexGrow: 1,
                                                    minHeight: '40px',
                                                    color: image.caption ? 'text.primary' : 'text.secondary',
                                                    fontStyle: image.caption ? 'normal' : 'italic'
                                                }}
                                            >
                                                {image.caption || 'No caption'}
                                            </Typography>
                                            <Tooltip title="Edit Caption">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => startEditingCaption(image)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Full Size Image Dialog */}
            <Dialog
                open={!!selectedImage}
                onClose={() => setSelectedImage(null)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {selectedImage?.caption || 'Guest Photo'}
                    </Typography>
                    <IconButton onClick={() => setSelectedImage(null)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {selectedImage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <img
                                src={selectedImage.image_url}
                                alt={selectedImage.caption || 'Guest photo'}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '80vh',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedImage(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}; 